import { useQuery } from "@tanstack/react-query";
import { getObjectPermissions } from "api/authorization";
import { useCallback, useMemo } from "react";
import { type IFgaObjectRelations, type TObjectType, type TObjectTypePermissions } from "types/authorization";

import { useFeatureFlags } from "./useFeatureFlags";

interface IUseAuthorizationReturn<T extends TObjectType> {
  error: Error | null;
  hasPermission: (permission: IFgaObjectRelations[T]) => boolean;
  loading: boolean;
  permissions: null | TObjectTypePermissions<T>;
  refetch: () => Promise<void>;
}

export const useAuthorization = <T extends TObjectType>(
  objectType: T,
  objectId: string | undefined,
): IUseAuthorizationReturn<T> => {
  const { enableOpenFgaAuthorization } = useFeatureFlags();
  const {
    data: permissions,
    error,
    isLoading: loading,
    refetch,
  } = useQuery({
    enabled: enableOpenFgaAuthorization && Boolean(objectId),
    queryFn: async () => {
      if (!enableOpenFgaAuthorization || !objectId) return null;
      const response = await getObjectPermissions(objectType, objectId);
      return response.permissions;
    },
    queryKey: ["authorization", objectType, objectId, enableOpenFgaAuthorization],
    staleTime: Infinity,
  });

  const hasPermission = useCallback(
    (permission: IFgaObjectRelations[T]): boolean => {
      if (!enableOpenFgaAuthorization) return true;
      if (!permissions) return false;
      return Boolean(permissions[permission]);
    },
    [permissions, enableOpenFgaAuthorization],
  );

  const refetchPermissions = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const normalizedPermissions = permissions ?? null;

  return useMemo(
    () => ({
      error,
      hasPermission,
      loading,
      permissions: normalizedPermissions,
      refetch: refetchPermissions,
    }),
    [error, hasPermission, loading, normalizedPermissions, refetchPermissions],
  );
};
